import React from "react";
import { Info, CreditCard, Briefcase, FileText, Rocket, Lightbulb, Loader2 } from "lucide-react";
import { getWaveLabel } from "../../../translations/translations";
import { sanitizeInput } from "../../utils/sanitize";

interface ExpenseFormProps {
  newExpense: any;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  addExpense: (data: any) => void;
  cancelEdit: () => void;
  editingId: string | null;
  loading: boolean;
  language: "en" | "es";
  t: any;
  expenseListFrequency: "weekly" | "biweekly" | "monthly";
  incomeSources: any[];
  wave1Amount: number;
  wave2Amount: number;
  wave3Amount: number;
  wave4Amount: number;
  theme?: "dark" | "light";
}

const inputClass =
  "w-full h-[42px] px-3 py-[0.625rem] text-[0.85rem] font-normal text-[var(--text-color)] bg-[var(--input-bg)] backdrop-blur-[10px] border border-[var(--border-color)] rounded-xl outline-none transition-all focus:border-primary focus:shadow-[0_0_0_4px_rgba(10,132,255,0.1)] focus:scale-[1.01] disabled:bg-[var(--input-disabled-bg)] disabled:opacity-60 disabled:cursor-not-allowed";

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  newExpense,
  handleChange,
  addExpense,
  cancelEdit,
  editingId,
  loading,
  language,
  t,
  expenseListFrequency,
  incomeSources,
  wave1Amount,
  wave2Amount,
  wave3Amount,
  wave4Amount,
  theme = "dark",
}) => {
  const selectStyle: React.CSSProperties = { colorScheme: theme === "dark" ? "dark" : "light" };
  // Sanitize expense name on change
  const handleSafeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (e.target.name === "name" && e.target instanceof HTMLInputElement) {
      // Sanitize text input to prevent XSS
      e.target.value = sanitizeInput(e.target.value);
    }
    handleChange(e);
  };
  return (
    <div className="grid grid-cols-2 gap-4 mt-4 max-md:grid-cols-1 max-md:gap-3">
      {/* Expense Name */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
          {t.expenseName}
          <span
            title={
              language === "en"
                ? "Enter the name of your bill or expense"
                : "Ingresa el nombre de tu factura o gasto"
            }
          >
            <Info size={16} className="text-primary cursor-help transition-all hover:scale-110" />
          </span>
        </label>
        <input
          type="text"
          name="name"
          className={inputClass}
          placeholder={
            language === "en"
              ? t.expenseForm.placeholder
              : t.expenseForm.placeholderEs
          }
          value={newExpense.name}
          onChange={handleSafeChange}
        />
      </div>

      {/* Amount */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
          {t.amount}
          <span
            title={
              language === "en"
                ? "Enter the exact amount you pay"
                : "Ingresa la cantidad exacta"
            }
          >
            <Info size={16} className="text-primary cursor-help transition-all hover:scale-110" />
          </span>
        </label>
        <input
          type="number"
          name="price"
          className={inputClass}
          placeholder="0.00"
          value={newExpense.price || ""}
          onChange={handleChange}
          onFocus={(e) => e.target.select()}
          step="0.01"
          min="0"
        />
      </div>

      {/* Recurring Expense Checkbox */}
      <div className="flex flex-col gap-2 min-w-0 relative" style={{ gridColumn: "1 / -1" }}>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="isRecurring"
            checked={newExpense.isRecurring || false}
            onChange={(e) => {
              handleChange({
                target: {
                  name: "isRecurring",
                  value: e.target.checked,
                  type: "checkbox",
                },
              } as any);
            }}
            disabled={loading}
          />
          <span style={{ marginLeft: "0.5rem" }}>
            {t.recurringExpense}
          </span>
          <span title={t.recurringExpenseTooltip}>
            <Info size={16} className="text-primary cursor-help transition-all hover:scale-110 ml-2" />
          </span>
        </label>
      </div>

      {/* Recurrence Frequency - Shown only when recurring is checked */}
      {newExpense.isRecurring && (
        <div className="flex flex-col gap-2 min-w-0 relative">
          <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
            {t.howOften}
          </label>
          <select
            name="recurrenceFrequency"
            className={`${inputClass} cursor-pointer appearance-none`}
            style={selectStyle}
            value={newExpense.recurrenceFrequency || "monthly"}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="weekly">{t.frequencyWeekly}</option>
            <option value="biweekly">{t.frequencyBiweekly}</option>
            <option value="monthly">{t.frequencyMonthly}</option>
            <option value="quarterly">{t.frequencyQuarterly}</option>
            <option value="semi-annual">{t.frequencySemiAnnual}</option>
            <option value="annual">{t.frequencyAnnual}</option>
          </select>
        </div>
      )}

      {/* Due Date */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label htmlFor="dueDate" className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
          {t.dueDate}
        </label>
        <input
          type="date"
          id="dueDate"
          name="dueDate"
          className={inputClass}
          value={newExpense.dueDate || ""}
          onChange={handleChange}
          disabled={loading}
          required
        />
      </div>

      {/* Income Source Selector */}
      {incomeSources.length > 0 && newExpense.category !== "Other" && (
        <div className="flex flex-col gap-2 min-w-0 relative">
          <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
            {language === "en"
              ? t.expenseForm.incomeSource
              : t.expenseForm.incomeSourceEs}
            <span
              title={
                language === "en"
                  ? "Select which income source"
                  : "Selecciona la fuente de ingreso"
              }
            >
              <Info size={16} className="text-primary cursor-help transition-all hover:scale-110" />
            </span>
          </label>
          <select
            name="incomeSourceId"
            className={`${inputClass} cursor-pointer appearance-none`}
            style={selectStyle}
            value={
              newExpense.incomeSourceId === ""
                ? "main-wave"
                : newExpense.incomeSourceId || ""
            }
            onChange={handleChange}
          >
            <option value="">
              {language === "en"
                ? t.expenseForm.selectDash
                : t.expenseForm.selectDashEs}
            </option>

            <option value="main-wave">
              {language === "en"
                ? expenseListFrequency === "weekly"
                  ? `Main Wave ($${wave1Amount.toFixed(2)}/$${wave2Amount.toFixed(2)}/$${wave3Amount.toFixed(2)}/$${wave4Amount.toFixed(2)})`
                  : `Main Wave ($${wave1Amount.toFixed(2)}/$${wave2Amount.toFixed(2)})`
                : expenseListFrequency === "weekly"
                  ? `Wave Principal ($${wave1Amount.toFixed(2)}/$${wave2Amount.toFixed(2)}/$${wave3Amount.toFixed(2)}/$${wave4Amount.toFixed(2)})`
                  : `Wave Principal ($${wave1Amount.toFixed(2)}/$${wave2Amount.toFixed(2)})`}
            </option>

            {incomeSources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name} ($
                {(source.amount1 ?? 0).toFixed(2)}
                {source.frequency !== "one-time" &&
                  `/$${(source.amount2 ?? 0).toFixed(2)}`}
                )
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Category */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
          {t.category}
          <span
            title={
              language === "en"
                ? "Wave: Regular paycheck | Paycheck: Additional income | Other: One-time"
                : "Wave: Cheque regular | Paycheck: Ingreso adicional | Otro: Único"
            }
          >
            <Info size={16} className="text-primary cursor-help transition-all hover:scale-110" />
          </span>
        </label>

        <div className="relative w-full min-h-[42px]">
          <div className="flex items-center gap-2 px-3 py-[0.625rem] bg-[var(--input-bg)] border border-[var(--border-color)] rounded-xl text-[0.85rem] pointer-events-none h-[42px] box-border backdrop-blur-[10px] transition-all category-preview">
            {newExpense.category === "Wave" && (
              <CreditCard size={20} className="text-[var(--text-color)] shrink-0" />
            )}
            {newExpense.category === "Paycheck" && (
              <Briefcase size={20} className="text-[var(--text-color)] shrink-0" />
            )}
            {newExpense.category === "Other" && (
              <FileText size={20} className="text-[var(--text-color)] shrink-0" />
            )}
            <span className="text-[var(--text-color)] whitespace-nowrap overflow-hidden text-ellipsis">
              {newExpense.category === "Wave"
                ? language === "en"
                  ? "Wave (Biweekly)"
                  : "Wave (Quincenal)"
                : newExpense.category === "Paycheck"
                  ? language === "en"
                    ? "Paycheck"
                    : "Paycheck"
                  : language === "en"
                    ? "Other (One-time)"
                    : "Otro (Único)"}
            </span>
          </div>
          <select
            name="category"
            className="absolute inset-0 opacity-0 cursor-pointer z-[1] w-full h-full category-select-overlay"
            style={selectStyle}
            value={newExpense.category}
            onChange={handleChange}
          >
            <option value="Wave">
              {language === "en"
                ? t.expenseForm.waveBiweekly
                : t.expenseForm.waveBiweeklyEs}
            </option>
            <option value="Paycheck">
              {language === "en"
                ? t.expenseForm.paycheck
                : t.expenseForm.paycheckEs}
            </option>
            <option value="Other">
              {language === "en"
                ? t.expenseForm.otherOneTime
                : t.expenseForm.otherOneTimeEs}
            </option>
          </select>
        </div>

        <p className="flex items-center gap-1 text-xs text-[var(--text-secondary)] mt-1 italic leading-tight">
          <Rocket size={16} />
          {language === "en"
            ? "Premium: Auto-categorize with AI"
            : "Premium: Auto-categorizar con IA"}
        </p>
      </div>

      {/* Wave Selection */}
      {newExpense.category === "Wave" && (
        <div className="flex flex-col gap-2 min-w-0 relative">
          <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
            {expenseListFrequency === "weekly" ? t.paymentWeek : t.paymentWave}
          </label>
          <select
            name="wave"
            className={`${inputClass} cursor-pointer appearance-none`}
            style={selectStyle}
            value={newExpense.wave || ""}
            onChange={handleChange}
          >
            {Array.from(
              { length: expenseListFrequency === "weekly" ? 4 : 2 },
              (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {getWaveLabel(i + 1, expenseListFrequency, t)}
                </option>
              )
            )}
          </select>
        </div>
      )}

      {/* Paycheck Frequency */}
      {newExpense.category === "Paycheck" && (
        <>
          <div className="flex flex-col gap-2 min-w-0 relative">
            <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">{t.paymentFrequency}</label>
            <select
              name="frequency"
              className={`${inputClass} cursor-pointer appearance-none`}
              style={selectStyle}
              value={newExpense.frequency}
              onChange={handleChange}
            >
              <option value="weekly">{t.weekly}</option>
              <option value="biweekly">{t.biweekly}</option>
              <option value="monthly">{t.monthly}</option>
            </select>
          </div>

          <div className="flex flex-col gap-2 min-w-0 relative">
            <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
              {newExpense.frequency === "weekly"
                ? t.paymentWeek
                : t.paymentWave}
            </label>
            <select
              name="wave"
              className={`${inputClass} cursor-pointer appearance-none`}
              style={selectStyle}
              value={newExpense.wave || ""}
              onChange={handleChange}
            >
              {Array.from(
                {
                  length:
                    newExpense.frequency === "weekly"
                      ? 4
                      : newExpense.frequency === "biweekly"
                        ? 2
                        : 1,
                },
                (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {getWaveLabel(i + 1, newExpense.frequency, t)}
                  </option>
                )
              )}
            </select>
          </div>
        </>
      )}

      {/* Payment Type */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">{t.paymentType}</label>
        <select
          name="paymentType"
          className={`${inputClass} cursor-pointer appearance-none`}
          style={selectStyle}
          value={newExpense.paymentType}
          onChange={handleChange}
        >
          <option value="url">{t.web}</option>
          <option value="app">{t.app}</option>
          <option value="autopay">{t.autopay}</option>
        </select>
      </div>

      {/* Payment URL */}
      <div className="flex flex-col gap-2 min-w-0 relative">
        <label className="flex items-center gap-1.5 text-[0.85rem] font-medium text-[var(--text-secondary)] m-0">
          {t.paymentUrl}
          <span
            title={
              language === "en"
                ? "Enter website/app link"
                : "Ingresa enlace del sitio/app"
            }
          >
            <Info size={16} className="text-primary cursor-help transition-all hover:scale-110" />
          </span>
        </label>
        <input
          type="text"
          name="paymentUrl"
          className={inputClass}
          placeholder={
            language === "en"
              ? newExpense.paymentType === "app"
                ? "e.g., venmo:// or cashapp://"
                : "e.g., www.chase.com"
              : newExpense.paymentType === "app"
                ? "ej., venmo:// o cashapp://"
                : "ej., www.chase.com"
          }
          value={newExpense.paymentUrl}
          onChange={handleChange}
          onFocus={(e) => e.target.select()}
          disabled={newExpense.paymentType === "autopay"}
        />
        <p className="flex items-center gap-1 text-xs text-[var(--text-secondary)] mt-1 italic leading-tight">
          <Lightbulb size={16} />
          {language === "en"
            ? newExpense.paymentType === "app"
              ? "Auto-converts to app deep links when you switch to 'App' mode"
              : "No need for https:// - just type www.example.com"
            : newExpense.paymentType === "app"
              ? "Se convierte automáticamente a enlaces de apps en modo 'App'"
              : "No necesitas https:// - solo escribe www.ejemplo.com"}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="col-span-full flex gap-3 mt-2 max-[640px]:flex-col">
        <button
          onClick={() => addExpense(newExpense)}
          className="px-6 py-2.5 bg-gradient-to-r from-primary to-primary-dark text-white border-none rounded-xl font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : editingId ? (
            t.updateExpense
          ) : (
            t.addExpense
          )}
        </button>
        {editingId && (
          <button
            onClick={cancelEdit}
            className="px-6 py-2.5 bg-transparent border border-[var(--border-color)] text-[var(--text-color)] rounded-xl font-semibold cursor-pointer hover:border-primary hover:text-primary transition-all"
          >
            {t.cancel}
          </button>
        )}
      </div>
    </div>
  );
};
